import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getStoryById } from '../services/firebase-stories';
import SceneCard from '../components/SceneCard';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { getAgeGroupSettings } from '../utils/age-groups';
import { formatAgeRange } from '../utils/formatters';
import './StoryViewPage.css';

const StoryViewPage = () => {
  const { storyId } = useParams();
  const navigate = useNavigate();

  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);

  // Fetch story on component mount or storyId change
  useEffect(() => {
    if (storyId) {
      fetchStory(storyId);
    }
  }, [storyId]);

  const fetchStory = async (id) => {
    try {
      setLoading(true);
      setError(null);
      
      const storyData = await getStoryById(id);
      
      if (!storyData) {
        throw new Error('Story not found');
      }
      
      setStory(storyData);
      console.log('Fetched story:', storyData);
    } catch (err) {
      console.error('Error fetching story:', err);
      setError({
        message: 'Could not load the story',
        details: err.message
      });
    } finally {
      setLoading(false);
    }
  };

  // Navigate to previous or next scene
  const handleSceneNavigation = (direction) => {
    if (!story || !story.scenes) return;
    
    if (direction === 'prev' && currentSceneIndex > 0) {
      setCurrentSceneIndex(currentSceneIndex - 1);
    } else if (direction === 'next' && currentSceneIndex < story.scenes.length - 1) {
      setCurrentSceneIndex(currentSceneIndex + 1);
    }
  };

  // Return to library
  const handleBackToLibrary = () => {
    navigate('/library');
  };

  // Print story
  const handlePrintStory = () => {
    window.print();
  };

  // Share story
  const handleShareStory = () => {
    // Use Web Share API if available
    if (navigator.share) {
      navigator.share({
        title: story ? story.title : 'My Fairy Tale',
        text: 'Check out this fairy tale I created!',
        url: window.location.href
      }).catch(err => {
        console.error('Error sharing:', err);
      });
    } else {
      // Fallback: copy link to clipboard
      navigator.clipboard.writeText(window.location.href)
        .then(() => {
          alert('Link copied to clipboard!');
        })
        .catch(err => {
          console.error('Error copying to clipboard:', err);
        });
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="story-view-page">
        <LoadingSpinner message="Loading your magical tale..." />
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="story-view-page">
        <ErrorMessage 
          message={error.message} 
          details={error.details} 
          onRetry={() => fetchStory(storyId)}
          onDismiss={handleBackToLibrary}
        />
      </div>
    );
  }

  // Render empty state if no story
  if (!story) {
    return (
      <div className="story-view-page">
        <div className="story-not-found">
          <h2>Story Not Found</h2>
          <p>We couldn't find the story you're looking for.</p>
          <button 
            className="pill-button primary"
            onClick={handleBackToLibrary}
          >
            Back to Library
          </button>
        </div>
      </div>
    );
  }

  // Get age group display info
  const ageGroup = story.ageGroup ? getAgeGroupSettings(story.ageGroup) : null;
  const ageRangeDisplay = ageGroup ? formatAgeRange(ageGroup.ageRange) : '';

  // Get current scene
  const currentScene = story.scenes && story.scenes[currentSceneIndex];

  return (
    <div className="story-view-page">
      {/* Print-only story header */}
      <div className="print-header">
        <h1>{story.title}</h1>
        <p>Created by StorySparkle</p>
      </div>

      {/* Story header */}
      <div className="story-header">
        <div className="story-header-content">
          {ageGroup && (
            <span className="story-age-tag">Ages {ageRangeDisplay}</span>
          )}
          <h1 className="story-title">{story.title}</h1>
          
          {story.moral && (
            <div className="story-moral">
              <strong>Moral:</strong> {story.moral}
            </div>
          )}
        </div>
      </div>

      {/* Story reading view */}
      <div className="story-reading-view">
        {/* Scene navigation */}
        <div className="scene-navigation">
          <button 
            className={`nav-button ${currentSceneIndex === 0 ? 'disabled' : ''}`}
            onClick={() => handleSceneNavigation('prev')}
            disabled={currentSceneIndex === 0}
            aria-label="Previous scene"
          >
            ←
          </button>
          <div className="scene-indicator">
            Scene {currentSceneIndex + 1} of {story.scenes ? story.scenes.length : 0}
          </div>
          <button 
            className={`nav-button ${currentSceneIndex === (story.scenes ? story.scenes.length - 1 : 0) ? 'disabled' : ''}`}
            onClick={() => handleSceneNavigation('next')}
            disabled={currentSceneIndex === (story.scenes ? story.scenes.length - 1 : 0)}
            aria-label="Next scene"
          >
            →
          </button>
        </div>

        {/* Current scene */}
        {currentScene && (
          <div className="current-scene">
            <SceneCard scene={currentScene} />
          </div>
        )}

        {/* Story actions */}
        <div className="story-actions">
          <button 
            className="pill-button outline"
            onClick={handleBackToLibrary}
            aria-label="Back to library"
          >
            ← Back to Library
          </button>
          <button 
            className="pill-button outline"
            onClick={handlePrintStory}
            aria-label="Print story"
          >
            🖨️ Print
          </button>
          <button 
            className="pill-button primary"
            onClick={handleShareStory}
            aria-label="Share story"
          >
            📤 Share Story
          </button>
        </div>
      </div>

      {/* Print-only full story (all scenes) */}
      <div className="print-only-content">
        <p className="print-moral"><strong>Moral:</strong> {story.moral}</p>
        {story.scenes && story.scenes.map((scene, index) => (
          <div key={index} className="print-scene">
            <img 
              src={scene.imageUrl} 
              alt={scene.imageKeywords || `Scene ${index + 1}`}
              className="print-image" 
            />
            <p className="print-text">{scene.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StoryViewPage;
